<template>
  <div>
    <div class="bp-chart">
      <analytics-chart-card
        title="Collector → Disburser (GBP per month)"
        :loading="loading || fxLoading"
        :error="chartError"
        empty-text="No companies to plot for these filters."
        :option="scatter.option"
        :table="scatter.table"
        @chart-click="openPoint"
      >
        <template #info>
          <p class="mb-1">
            One dot per company. Left to right: the share of its counterparties that are payees, from only
            payers (Collector) to only payees (Disburser). Up: money moved per month, Collections and
            Disbursements per month × their average amount.
          </p>
          <p class="mb-1">
            Converted to GBP at today's mid-market rates<template v-if="fxAsOf"> ({{ fxAsOf }})</template>, the
            only place Business Profiles converts currencies. The table below stays in each company's own
            currency.
          </p>
          <p class="mb-0">
            Follows the table's filters.<template v-if="scatter.leftOff">
              {{ formatCount(scatter.leftOff) }} {{ scatter.leftOff === 1 ? "company is" : "companies are" }} left
              off because a currency has no rate.</template
            >
          </p>
        </template>
      </analytics-chart-card>
    </div>
    <div class="card bp-card">
      <div class="bp-header ba-accent-header">
        <h2 class="bp-title">Company profiles</h2>
        <button
          type="button"
          class="btn btn-sm ba-view-toggle bp-export"
          :disabled="exporting || loading || !!error || !rows.length"
          @click="exportCsv"
        >
          <b-spinner v-if="exporting" small class="mr-1" label="Exporting"></b-spinner>
          <i v-else class="fa fa-download mr-1" aria-hidden="true"></i>
          Export CSV
        </button>
      </div>

      <div class="bp-body">
        <div v-if="exportError" class="alert alert-danger py-2" role="alert">
          <i class="fa fa-exclamation-triangle mr-2" aria-hidden="true"></i>{{ exportError }}
        </div>

        <div class="bp-filters">
          <label v-for="f in filterControls" :key="f.key" class="bp-filter">
            <span class="bp-filter-label">{{ f.label }}</span>
            <b-form-select v-model="filters[f.key]" :options="f.options" size="sm" :disabled="loading || !!error" />
          </label>
          <button
            v-if="hasFilters"
            type="button"
            class="btn btn-link btn-sm bp-clear"
            @click="filters = emptyFilters()"
          >
            Clear filters
          </button>
        </div>

        <div v-if="loading" class="bp-state">
          <b-spinner small label="Loading"></b-spinner>
          <span class="ml-2">Loading…</span>
        </div>
        <div v-else-if="error" class="bp-state bp-state-error" role="alert">
          <i class="fa fa-exclamation-triangle mr-2" aria-hidden="true"></i>{{ error }}
          <button type="button" class="btn btn-sm btn-outline-secondary ml-3" @click="load">Retry</button>
        </div>
        <template v-else>
          <div class="d-flex justify-content-between align-items-center flex-wrap mb-2">
            <span class="bp-count" role="status">
              {{ formatCount(rows.length) }}
              <template v-if="hasFilters">of {{ formatCount(companies.length) }}</template>
              {{ companies.length === 1 ? "company" : "companies" }}
            </span>
            <b-pagination
              v-if="rows.length > perPage"
              v-model="currentPage"
              class="pagination-small mb-0"
              :total-rows="rows.length"
              :per-page="perPage"
              first-text="First"
              last-text="Last"
              align="right"
            ></b-pagination>
          </div>
          <div class="bp-table-wrap">
            <b-table
              striped
              show-empty
              hover
              table-class="mb-0 table-custom border"
              head-variant="light"
              :items="rows"
              :fields="fields"
              :per-page="perPage"
              :current-page="currentPage"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              sort-compare-locale="en"
              primary-key="businessId"
              tbody-tr-class="bp-row"
              :empty-text="companies.length ? 'No companies match these filters.' : 'No companies yet.'"
              @row-clicked="openBusiness"
            >
              <template #head()="{ label, field }">
                <!-- Group names sit above each group's first column: one header row, so nothing can gap. -->
                <span class="bp-group-label" :aria-hidden="field.group ? null : 'true'">{{ field.group || "\u00a0" }}</span>
                {{ label }}
                <i
                  v-if="field.sortable && sortBy === field.key"
                  :class="sortDesc ? 'fa fa-long-arrow-down' : 'fa fa-long-arrow-up'"
                  class="ml-1"
                  aria-hidden="true"
                ></i>
              </template>
              <template #cell(name)="{ item, value }">
                <router-link :to="businessLink(item)" class="font-weight-bold" @click.native.stop>{{ value }}</router-link>
              </template>
              <template #cell(collectionsSize)="{ item }">{{ item.collectionsSizeText }}</template>
              <template #cell(disbursementsSize)="{ item }">{{ item.disbursementsSizeText }}</template>
            </b-table>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {
  exportBusinessProfiles,
  fetchBusinessProfiles,
  fetchBusinessProfilesFxRates,
} from "@/services/businessAnalyticsService";
import AnalyticsChartCard from "./AnalyticsChartCard.vue";
import { formatCount, formatMoney, profileScatterOption } from "./chartTheme";
import {
  DIRECTIONS,
  REACH_BANDS,
  SIZE_BANDS,
  chartPoints,
  countryOptions,
  emptyFilters,
  exportFileName,
  filterProfiles,
  formatPerMonth,
  profileFilterParams,
  profileRow,
  reachBandLabel,
} from "./businessProfiles";

function errorMessage(err, fallback) {
  const status = err && err.response && err.response.status;
  if (status === 403) return "You don't have permission to view Business Profiles.";
  const data = err && err.response && err.response.data;
  // A failed export's body is a Blob, so there's no message to read from it.
  return (data && typeof data.message === "string" && data.message) || fallback;
}

const perMonth = { formatter: (value) => formatPerMonth(value), sortable: true, tdClass: "text-right" };
const count = { formatter: (value) => formatCount(value), sortable: true, tdClass: "text-right" };
const groupStart = "bp-group-start";

export default {
  name: "BusinessProfilesCard",
  components: { "analytics-chart-card": AnalyticsChartCard },
  data() {
    return {
      profiles: null,
      loading: false,
      // The chart's rates load separately, so the table never waits on them.
      fx: null,
      fxLoading: false,
      fxError: "",
      error: "",
      filters: emptyFilters(),
      sortBy: "name",
      sortDesc: false,
      perPage: 50,
      currentPage: 1,
      exporting: false,
      exportError: "",
    };
  },
  computed: {
    companies() {
      return (this.profiles && this.profiles.companies) || [];
    },
    limits() {
      return (this.profiles && this.profiles.reachLimits) || { lowMax: 20, mediumMax: 100 };
    },
    filteredCompanies() {
      return filterProfiles(this.companies, this.filters);
    },
    rows() {
      return this.filteredCompanies.map(profileRow);
    },
    fxAsOf() {
      return this.fx ? this.fx.asOf : "";
    },
    chartError() {
      return this.error || this.fxError;
    },
    /** The Collector → Disburser chart and its table view, from the companies the filters show. */
    scatter() {
      const { points, leftOff } = chartPoints(this.filteredCompanies, this.fx);
      if (!points.length) return { option: null, table: { columns: [], rows: [] }, leftOff };
      const money = (value) => formatMoney(value, "GBP");
      const axisMoney = (value) =>
        new Intl.NumberFormat("en-GB", {
          style: "currency",
          currency: "GBP",
          notation: "compact",
          maximumFractionDigits: 1,
        }).format(value);
      return {
        option: profileScatterOption({ points, format: money, axisFormat: axisMoney }),
        table: {
          columns: ["Payees share", "Moved per month"],
          rows: [...points]
            .sort((a, b) => b.y - a.y)
            .map((p) => ({ label: p.name, note: p.direction, cells: [`${Math.round(p.x)}%`, money(p.y)] })),
        },
        leftOff,
      };
    },
    hasFilters() {
      return Object.values(this.filters).some(Boolean);
    },
    filterControls() {
      const any = { value: "", text: "Any" };
      const reach = [any, ...REACH_BANDS.map((b) => ({ value: b, text: reachBandLabel(b, this.limits) }))];
      const size = [any, ...SIZE_BANDS];
      return [
        { key: "direction", label: "Direction", options: [any, ...DIRECTIONS] },
        { key: "country", label: "Country", options: [any, ...countryOptions(this.companies)] },
        { key: "collectionsReachBand", label: "Collections: payers", options: reach },
        { key: "disbursementsReachBand", label: "Disbursements: payees", options: reach },
        { key: "collectionsSizeBand", label: "Collections: size", options: size },
        { key: "disbursementsSizeBand", label: "Disbursements: size", options: size },
      ];
    },
    fields() {
      return [
        { key: "name", label: "Company", sortable: true },
        { key: "country", label: "Country", sortable: true },
        { key: "direction", label: "Direction", sortable: true },
        { key: "collectionsPerMonth", label: "Per month", group: "Collections", ...perMonth, class: groupStart },
        { key: "payers", label: "Payers", ...count },
        { key: "collectionsAverage", label: "Average", tdClass: "text-right" },
        { key: "collectionsSize", label: "Size", sortable: true },
        { key: "disbursementsPerMonth", label: "Per month", group: "Disbursements", ...perMonth, class: groupStart },
        { key: "payees", label: "Payees", ...count },
        { key: "disbursementsAverage", label: "Average", tdClass: "text-right" },
        { key: "disbursementsSize", label: "Size", sortable: true },
        {
          key: "topUps",
          label: "Deposits",
          group: "Wallet habits (per month)",
          ...perMonth,
          class: `${groupStart} bp-wallet`,
        },
        { key: "withdrawals", label: "Withdrawals", ...perMonth, class: "bp-wallet" },
        { key: "conversions", label: "Conversions", ...perMonth, class: "bp-wallet" },
      ];
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.currentPage = 1;
        this.exportError = "";
      },
    },
  },
  created() {
    this.load();
    this.loadFx();
  },
  methods: {
    formatCount,
    emptyFilters,
    businessLink(item) {
      return `/business/${item.businessId}/financials`;
    },
    openPoint(event) {
      const point = event && event.data && event.data.point;
      if (point) this.openBusiness(point);
    },
    openBusiness(item) {
      this.$router.push(this.businessLink(item));
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        this.profiles = await fetchBusinessProfiles();
      } catch (err) {
        this.error = errorMessage(err, "Couldn't load Business Profiles. Please try again.");
      } finally {
        this.loading = false;
      }
    },
    async loadFx() {
      this.fxLoading = true;
      this.fxError = "";
      try {
        this.fx = await fetchBusinessProfilesFxRates();
      } catch (err) {
        this.fxError = "Exchange rates couldn't be loaded, so this chart can't be drawn. The table below is unaffected.";
      } finally {
        this.fxLoading = false;
      }
    },
    async exportCsv() {
      this.exporting = true;
      this.exportError = "";
      try {
        const { blob, filename } = await exportBusinessProfiles(profileFilterParams(this.filters));
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename || exportFileName();
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        this.exportError = errorMessage(err, "Couldn't export Business Profiles. Please try again.");
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
/* The chart card sizes itself; it just needs the page's gap below it. */
.bp-chart {
  margin-bottom: 24px;
}
.bp-card {
  margin-bottom: 24px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(33, 47, 63, 0.12);
}
.bp-header {
  height: 52px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
}
.bp-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: inherit;
}
.bp-export {
  white-space: nowrap;
}
.bp-body {
  padding: 16px;
}
.bp-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 16px;
}
.bp-filter {
  display: flex;
  flex-direction: column;
  margin: 0;
  min-width: 150px;
}
.bp-filter-label {
  font-size: 12px;
  margin-bottom: 2px;
  opacity: 0.8;
}
.bp-clear {
  padding-bottom: 6px;
}
.bp-count {
  font-size: 13px;
  opacity: 0.85;
}
.bp-state {
  min-height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  opacity: 0.8;
}
.bp-state-error {
  color: #c0392b;
  opacity: 1;
}
/*
 * The wrapper is the table's scroll box (both ways), so the header row sticks to its top while the
 * rows scroll under it. One row only: Chrome paints two stacked sticky rows with a see-through gap.
 */
.bp-table-wrap {
  max-height: 70vh;
  overflow: auto;
}
.bp-table-wrap >>> table {
  margin-bottom: 0;
  /* Collapsed borders leave see-through gaps in a sticky header. */
  border-collapse: separate;
  border-spacing: 0;
}
.bp-table-wrap >>> thead th {
  /* !important: the app's table styles make header cells position: relative. */
  position: sticky !important;
  top: 0;
  z-index: 2;
}
/* A group's name may run past its first column, so that cell sits above the ones after it. */
.bp-table-wrap >>> thead th.bp-group-start {
  z-index: 3;
}
.bp-group-label {
  display: block;
  /* Zero width: a long group name runs over the next columns instead of widening its own. */
  width: 0;
  overflow: visible;
  margin-bottom: 4px;
  font-weight: 600;
}
/* Collections, Disbursements and Wallet habits each start with a rule, so the groups read apart. */
.bp-table-wrap >>> .bp-group-start {
  border-left: 2px solid rgba(128, 128, 128, 0.45) !important;
}
.bp-table-wrap >>> thead th {
  text-align: left;
}
/* A light line between every column; the groups keep their heavier rule. */
.bp-table-wrap >>> td + td,
.bp-table-wrap >>> th + th {
  border-left: 1px solid rgba(128, 128, 128, 0.2);
}
.bp-table-wrap >>> td,
.bp-table-wrap >>> th {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
/* Wallet habits are a different kind of activity from Collections and Disbursements: tint them. */
.bp-table-wrap >>> .bp-wallet {
  /* An image layer, not background-color, so the sticky header cells stay opaque. */
  background-image: linear-gradient(rgba(110, 182, 68, 0.08), rgba(110, 182, 68, 0.08));
}
.bp-table-wrap >>> .bp-row {
  cursor: pointer;
}
</style>

<style src="./accentHeader.css"></style>
